import { useRef, type Dispatch, type FormEvent, type SetStateAction } from 'react';
import { toast } from 'sonner';
import type { ExecutionMode } from '@hangar/shared';
import { ApiError } from '@/api/client';
import { useProfileMutations } from '@/api/hooks';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { serverErrorsOf } from '@/lib/formErrors';
import { notAvailableYet } from '@/lib/mutationError';
import { showErrorToast } from '@/lib/toasts';
import { EXECUTION_MODE_LABEL } from '@/components/badges';
import { useSeesProviders } from '@/lib/useAudience';
import type { ProfileFormValues, ProjectEnvironment } from './profileFormModel';
import { boundEnvironments, buildProfileBody, mergeServerErrors, profileNameErrors, type ProfileBody } from './profilePayload';
import type { FormValidation } from './profileFormValidation';

/** The feedback state a submit drives: it clears it on start and fills it from a failure. */
export interface FormFeedback {
  setSubmitted: (v: boolean) => void;
  setProblem: (v: unknown) => void;
  setServer: Dispatch<SetStateAction<Record<string, string>>>;
  setAdvanced: (v: boolean) => void;
}

interface SubmitInput {
  editing?: Profile;
  onDone: () => void;
  appId: string;
  values: ProfileFormValues;
  validation: FormValidation;
  environments: ProjectEnvironment[];
  account: ProviderAccount | undefined;
  allowedModes: ExecutionMode[];
  feedback: FormFeedback;
}

/** What the API said about a failed save, turned into what the form shows (named 422 fields, form problem, toast). */
function applyFailure(err: unknown, bound: ProjectEnvironment[], environments: ProjectEnvironment[], feedback: FormFeedback): void {
  const unavailable = notAvailableYet(err);
  if (unavailable) {
    feedback.setProblem(unavailable);
    return;
  }
  const mapped = serverErrorsOf(err);
  const envIdOfSlug = (slug: string) => environments.find((env) => env.slug === slug)?.id;
  const named = err instanceof ApiError ? profileNameErrors(err, bound.map((env) => env.id), envIdOfSlug) : {};
  const fields = mergeServerErrors(mapped.fields, named);
  feedback.setServer(fields);
  if (Object.keys(fields).length === 0) feedback.setProblem(err);
  if (mapped.toast) showErrorToast(mapped.toast);
}

/** Creates (with an idempotency key kept until it succeeds) or updates the profile and announces it with a toast. */
function useSaveProfile(appId: string, editing: Profile | undefined, values: ProfileFormValues, account: ProviderAccount | undefined) {
  const sees = useSeesProviders();
  const { create, update } = useProfileMutations(appId);
  const idempotencyKey = useRef(crypto.randomUUID());
  const save = async (body: ProfileBody) => {
    if (editing) {
      await update.mutateAsync({ profileId: editing.id, body, etag: editing.etag });
      toast.success(`${body.name} updated`);
      return;
    }
    await create.mutateAsync({ body: { ...body, slug: values.slug, platform: values.platform }, idempotencyKey: idempotencyKey.current });
    idempotencyKey.current = crypto.randomUUID();
    const modes = body.executionModes.map((m) => EXECUTION_MODE_LABEL[m]).join(', ');
    // The provider account is named for platform operators only (SPEC decision 23).
    toast.success(`Profile ${values.slug} created`, { description: [values.platform, sees ? account?.name : undefined, modes].filter(Boolean).join(' · ') });
  };
  return { save, pending: create.isPending || update.isPending };
}

/** Validates, builds the body and creates or updates the profile; `pending` covers both mutations. */
export function useProfileSubmit(input: SubmitInput) {
  const { editing, values, validation, environments, account, feedback } = input;
  const { save, pending } = useSaveProfile(input.appId, editing, values, account);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    feedback.setSubmitted(true);
    feedback.setProblem(null);
    feedback.setServer({});
    if (validation.invalid) {
      if (validation.local.sizeBudgetMb || validation.local.launchForm) feedback.setAdvanced(true);
      return;
    }
    try {
      await save(buildProfileBody(values, { accountKind: account?.kind, allowedModes: input.allowedModes, environments }));
      input.onDone();
    } catch (err) {
      applyFailure(err, boundEnvironments(values, environments), environments, feedback);
    }
  };
  return { onSubmit, pending };
}
